import {FC, useMemo} from "react";
import {ChevronDown24Regular, LockClosedRegular} from "@fluentui/react-icons";
import IntelligenceGlyph from "./IntelligenceGlyph";
import {BrandSparkleIcon} from "./icons";
import IntelligencePromptBar from "./IntelligencePromptBar";
import InsightAgentBadge from "../InsightAgentBadge";
import {InsightTeaser, SuggestedPrompt} from "./intelligenceModels";
import {getAgentIcon} from "../../insightAgents";
import type {AgentIdentity} from "../../insightAgents";
import type {BentoPersona} from "../../bentoPersona";
import {COACHES} from "../../coachCatalog";
import {useCoachSettings} from "../../coachSettingsStore";

/** Text up to (and including) the first sentence-ending punctuation, falling back to the whole string. */
const getFirstSentence = (text: string): string => {
    const match = /^.*?[.!?](?=\s|$)/.exec(text.trim());
    return match ? match[0] : text.trim();
};

interface IntelligenceEmptyStateProps {
    userName: string;
    agent?: AgentIdentity | null;
    persona: BentoPersona;
    insightTeasers: InsightTeaser[];
    isInsightsLoading: boolean;
    onViewAllInsights: () => void;
    suggestions: SuggestedPrompt[];
    isSuggestionDisabled?: (prompt: SuggestedPrompt) => boolean;
    onRunSuggested: (prompt: SuggestedPrompt) => void;
    promptValue: string;
    promptDisabled: boolean;
    onPromptChange: (value: string) => void;
    onSendPrompt: () => void;
    onSavePrompt: () => void;
}

const IntelligenceEmptyState: FC<IntelligenceEmptyStateProps> = ({
    userName,
    agent,
    persona,
    insightTeasers,
    isInsightsLoading,
    onViewAllInsights,
    suggestions,
    isSuggestionDisabled,
    onRunSuggested,
    promptValue,
    promptDisabled,
    onPromptChange,
    onSendPrompt,
    onSavePrompt
}) => {
    const AgentIcon = agent ? getAgentIcon(agent) : undefined;
    const isNoCoachesEnabled = persona.id === "no_coaches_enabled";
    const enabledSuggestionCount = suggestions.filter(
        (suggestion) => !(isSuggestionDisabled?.(suggestion) ?? false)
    ).length;
    const [featuredTeaser] = insightTeasers;
    const FeaturedAgentIcon = featuredTeaser?.agent
        ? getAgentIcon(featuredTeaser.agent, featuredTeaser.card.id)
        : undefined;

    const coachSettings = useCoachSettings();
    const {enabledCoachPills, unavailableCoachCount} = useMemo(() => {
        const enabled: {id: string; name: string}[] = [];
        let totalProductCount = 0;
        COACHES.forEach((coach) => {
            const isCoachVisible = persona.visibleCoachIds.includes(coach.id);
            coach.products.forEach((product) => {
                totalProductCount += 1;
                const isEnabled =
                    !isNoCoachesEnabled &&
                    isCoachVisible &&
                    Boolean(coachSettings[`${coach.id}:${product.id}`]);
                if (isEnabled) {
                    enabled.push({id: `${coach.id}:${product.id}`, name: product.name});
                }
            });
        });
        return {
            enabledCoachPills: enabled,
            unavailableCoachCount: totalProductCount - enabled.length
        };
    }, [coachSettings, isNoCoachesEnabled, persona.visibleCoachIds]);

    return (
        <div className="pi-empty">
            <div className="pi-empty__coaches">
                <span className="pi-section-label pi-empty__coaches-label">
                    Coaches
                </span>
                <div className="pi-empty__coaches-pills">
                    {enabledCoachPills.map((pill) => (
                        <span key={pill.id} className="pi-coach-pill">
                            {pill.name} •
                        </span>
                    ))}
                    {unavailableCoachCount > 0 && (
                        <span className="pi-coach-pill pi-coach-pill--disabled">
                            +{unavailableCoachCount} available
                        </span>
                    )}
                </div>
            </div>
            <span
                className={`pi-empty__eyebrow${
                    agent ? " pi-empty__eyebrow--agent" : ""
                }`}
                style={
                    agent
                        ? ({"--par-agent": agent.accent} as React.CSSProperties)
                        : undefined
                }
            >
                {AgentIcon ? <AgentIcon /> : <BrandSparkleIcon />}
                {agent ? agent.name : "PAR Intelligence"}
            </span>
            <h2 className="pi-empty__title">
                {agent ? (
                    <>
                        Hi {userName}, I&apos;m {agent.name}. <br />
                        What should we dig into?
                    </>
                ) : (
                    <>
                        Good morning, {userName}. <br />
                        What should we look into?
                    </>
                )}
            </h2>
            <p className="pi-empty__subtitle">
                {agent
                    ? `I specialize in ${agent.specialty.toLowerCase()}. Ask me anything in that area across your locations - or pick a starting point below.`
                    : isNoCoachesEnabled
                    ? `Ask anything about ${persona.subtitleDomains} — or pick a starting point below.`
                    : `Ask anything about ${persona.subtitleDomains} across your locations — or pick a starting point below.`}
            </p>

            {isNoCoachesEnabled && (
                <p className="pi-empty__punchh-note">
                    PAR Intelligence can walk you through setup, campaigns, and
                    reporting inside Punchh.
                </p>
            )}

            <IntelligencePromptBar
                value={promptValue}
                disabled={promptDisabled}
                onChange={onPromptChange}
                onSend={onSendPrompt}
                onSave={onSavePrompt}
                variant="inline"
            />

            <details className="pi-suggest-accordion">
                <summary className="pi-suggest-accordion__summary">
                    {enabledSuggestionCount} suggested questions for{" "}
                    {isNoCoachesEnabled ? "Platform Help" : persona.intelligenceRole}
                    <span className="pi-suggest-accordion__hint">
                        {persona.suggestionHint}
                    </span>
                    <ChevronDown24Regular className="pi-suggest-accordion__chevron" />
                </summary>
                <div className="pi-suggest__grid">
                    {suggestions.map((suggestion) => {
                        const isDisabled =
                            isSuggestionDisabled?.(suggestion) ?? false;
                        return (
                            <button
                                type="button"
                                key={suggestion.id}
                                className={`pi-suggest-card${
                                    isDisabled ? " pi-suggest-card--disabled" : ""
                                }`}
                                disabled={isDisabled}
                                title={
                                    isDisabled
                                        ? "Enable a coach product to run this prompt"
                                        : suggestion.title
                                }
                                onClick={() => onRunSuggested(suggestion)}
                            >
                                <span className="pi-suggest-card__icon">
                                    <IntelligenceGlyph icon={suggestion.icon} />
                                </span>
                                <span className="pi-suggest-card__category">
                                    {suggestion.category}
                                </span>
                                <span className="pi-suggest-card__title">
                                    {suggestion.title}
                                </span>
                                {isDisabled && (
                                    <span className="pi-suggest-card__lock-hint">
                                        <LockClosedRegular />
                                        Coach required
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </details>

            <button
                type="button"
                className="pi-insight-teaser"
                onClick={onViewAllInsights}
                disabled={isInsightsLoading}
            >
                {isNoCoachesEnabled ? (
                    <span className="pi-insight-teaser__empty">
                        <span className="pi-insight-teaser__empty-label">
                            No coaches enabled
                        </span>
                        <span className="pi-insight-teaser__empty-text">
                            PAR Intelligence can only answer help and
                            documentation questions - not questions about your
                            data.
                        </span>
                    </span>
                ) : isInsightsLoading ? (
                    <span className="pi-insight-teaser__empty">
                        <span
                            className="pi-activity__spinner"
                            aria-hidden="true"
                        />
                        Loading insights…
                    </span>
                ) : featuredTeaser ? (
                    <>
                        <div className="pi-insight-teaser__top">
                            <InsightAgentBadge
                                icon={
                                    FeaturedAgentIcon ? (
                                        <FeaturedAgentIcon />
                                    ) : (
                                        <BrandSparkleIcon />
                                    )
                                }
                                name={featuredTeaser.agent?.name ?? "PAR Intelligence"}
                                note="noticed this in your data"
                            />
                            <span className="pi-insight-teaser__meta">
                                <span className="pi-insight-teaser__count">
                                    1 of {insightTeasers.length}
                                </span>
                                <span className="pi-insight-teaser__link">
                                    View all {insightTeasers.length} →
                                </span>
                            </span>
                        </div>
                        <p className="pi-insight-teaser__text">
                            {getFirstSentence(featuredTeaser.card.narrative)}
                        </p>
                    </>
                ) : (
                    <span className="pi-insight-teaser__empty">
                        No insights are waiting for you
                    </span>
                )}
            </button>
        </div>
    );
};

export default IntelligenceEmptyState;
